/**
 * STRICT Vehicle → Plate Size Mapping
 * 
 * This is the SINGLE SOURCE OF TRUTH for which sizes are valid for each vehicle type.
 * Vehicle type changes MUST trigger complete recalculation of available sizes.
 */

import { type VehicleCategory, type PlateSize } from './plateRulesEngine'

/**
 * Strict mapping: Vehicle Category → Allowed Plate Sizes
 * 
 * When vehicle type changes:
 * - ALL plate sizes must be recalculated from this mapping
 * - Invalid sizes must disappear (not disabled)
 * - Plate preview must update instantly
 * - Invalid inputs must reset
 */
export const VEHICLE_SIZE_MAPPING: Record<VehicleCategory, PlateSize[]> = {
  // PKW (Personenkraftwagen)
  PKW: [
    // Single-line plates
    { width: 520, height: 110, layout: 'einzeilig' },
    { width: 480, height: 110, layout: 'einzeilig' },
    { width: 460, height: 110, layout: 'einzeilig' },
    { width: 420, height: 110, layout: 'einzeilig' },
    { width: 400, height: 110, layout: 'einzeilig' },
    // Two-line plates
    { width: 520, height: 200, layout: 'zweizeilig' },
    { width: 340, height: 200, layout: 'zweizeilig' },
    { width: 320, height: 200, layout: 'zweizeilig' },
    { width: 280, height: 200, layout: 'zweizeilig' },
  ],

  // LKW (Lastkraftwagen) - Same as PKW
  TRUCK: [
    // Single-line plates
    { width: 520, height: 110, layout: 'einzeilig' },
    { width: 480, height: 110, layout: 'einzeilig' },
    { width: 460, height: 110, layout: 'einzeilig' },
    { width: 420, height: 110, layout: 'einzeilig' },
    { width: 400, height: 110, layout: 'einzeilig' },
    // Two-line plates
    { width: 520, height: 200, layout: 'zweizeilig' },
    { width: 340, height: 200, layout: 'zweizeilig' },
    { width: 320, height: 200, layout: 'zweizeilig' },
    { width: 280, height: 200, layout: 'zweizeilig' },
  ],

  // Motorrad (Motorcycles) - Width x Height (200mm)
  MOTORCYCLE: [
    { width: 180, height: 200, layout: 'zweizeilig' },
    { width: 200, height: 200, layout: 'zweizeilig' },
    { width: 220, height: 200, layout: 'zweizeilig' },
  ],

  // Leichtkraftrad 125cc - Width x Height (130mm)
  LEICHTKRAFT: [
    { width: 255, height: 130, layout: 'zweizeilig' }, // Standard Size
  ],

  // Anhänger (Trailer) - Same as PKW
  TRAILER: [
    // Single-line plates
    { width: 520, height: 110, layout: 'einzeilig' },
    { width: 480, height: 110, layout: 'einzeilig' },
    { width: 460, height: 110, layout: 'einzeilig' },
    { width: 420, height: 110, layout: 'einzeilig' },
    { width: 400, height: 110, layout: 'einzeilig' },
    // Two-line plates
    { width: 520, height: 200, layout: 'zweizeilig' },
    { width: 340, height: 200, layout: 'zweizeilig' },
    { width: 320, height: 200, layout: 'zweizeilig' },
    { width: 280, height: 200, layout: 'zweizeilig' },
  ],

  // Traktor (Landwirtschaft) - Width x Height (130mm)
  TRAKTOR: [
    { width: 255, height: 130, layout: 'zweizeilig' }, // Standard Wide
    { width: 240, height: 130, layout: 'zweizeilig' }, // Narrower option
  ],
}

/**
 * Get valid plate sizes for a vehicle category
 * This is the ONLY way to get sizes - always use this function
 */
export function getValidSizesForVehicle(vehicleCategory: VehicleCategory): PlateSize[] {
  return VEHICLE_SIZE_MAPPING[vehicleCategory] || []
}

/**
 * Check if a plate size is valid for a vehicle category
 */
export function isValidSizeForVehicle(
  vehicleCategory: VehicleCategory,
  plateSize: PlateSize
): boolean {
  const validSizes = getValidSizesForVehicle(vehicleCategory)
  return validSizes.some(
    size =>
      size.width === plateSize.width &&
      size.height === plateSize.height &&
      size.layout === plateSize.layout
  )
}

/**
 * Get plate size label for display
 * Format: width × height (without "mm" or layout description)
 */
export function getPlateSizeLabel(size: PlateSize): string {
  return `${size.width} × ${size.height}`
}
